import React, { useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import {
  Provider, defaultTheme, ProgressCircle,
} from '@adobe/react-spectrum';
import Header from './Header';
import ProductPage from './ProductPage';
import MarketoInstancePage from './MarketoInstancePage';
import AmazonChatWidget from './AmazonChatWidget';
import ServiceErrorScreen from './ServiceErrorScreen';
import BackendService from '../services/BackendService';
import PostMessageUtil from '../utils/PostMessageUtil';
import '../assets/styles/style.scss';
import GlobalData from '../utils/GlobalData';
import Logger from '../utils/Logger';
import Config from '../config/env_variables';
import { chatWidgetDefaults } from '../utils/constants';

// URL override for testing (e.g. ?uiVersion=v2) - takes priority over the host's INITIAL_DATA value
const getUiVersionFromUrl = () => {
  const param = new URLSearchParams(window.location.search).get('uiVersion');
  return param === 'v2' || param === 'v1' ? param : null;
};

export default function Index() {
  const COMPONENT = {
    LOADING: 'loading',
    PRODUCTS: 'products',
    SERVICE_ERROR: 'serviceError',
    MARKETO_INSTANCES: 'marketoInstances',
    AMAZON_CHAT_WIDGET: 'amazonChatWidget',
  };

  let backgroundClass;
  let activeComponent;

  const [messageData, setMessageData] = useState([]);
  const [products, setProductsData] = useState([]);
  const [marketoInstances, setMarketoInstances] = useState([]);
  const [marketoAsSelectedProduct, setMarketoAsSelectedProduct] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [flowId, setFlowId] = useState('');
  const [connectInstanceId, setConnectInstanceId] = useState('');
  const [componentType, setComponentType] = useState(COMPONENT.LOADING);
  const [isMarketoProcessed, setMarketoProcess] = useState(false);
  const [uiVersion, setUiVersion] = useState(() => getUiVersionFromUrl() || 'v1');

  // Error Handler
  const errorHandler = () => {
    setComponentType(COMPONENT.SERVICE_ERROR);
  };

  const getAvailableAgentCount = (product) => {
    Logger.debug('product in getAgentCount: ', product);

    if (!product || Object.keys(product).length === 0) {
      Logger.error('product array does not meet requirement for agent count!');
      errorHandler();
      return;
    }

    setSelectedProduct(product.title);
    setFlowId(product.flowId);
    setConnectInstanceId(product.instanceId || '');
    setComponentType(COMPONENT.AMAZON_CHAT_WIDGET);
  };

  // Product selection handler
  const handleOnProductSelect = (product) => {
    // if Marketo as a select product, Display Marketo instances
    Logger.debug(`Selected Product: ${JSON.stringify(product)}`);
    if (product && Object.keys(product).length > 0 && product.title.toLowerCase() === 'marketo' && !isMarketoProcessed) {
      setMarketoAsSelectedProduct(product);
      setMarketoProcess(true);

      if (Object.keys(marketoInstances).length < 2) {
        setSelectedInstance((Object.keys(marketoInstances).length === 1) ? marketoInstances[0].instanceId : '');
        getAvailableAgentCount(product);
      } else setComponentType(COMPONENT.MARKETO_INSTANCES);
    } else getAvailableAgentCount(product);
  };

  const handleOnMarketoInstanceSelect = (instance) => {
    setSelectedInstance(instance.instanceId);
    handleOnProductSelect(marketoAsSelectedProduct);
  };

  const getEntitlements = (message) => {
    BackendService.getEntitledProducts(message.imsAccessToken,
      message.imsUserProfile.userId, GlobalData.language,
      message.orgId, message.imsUserProfile.email, message.chatSurface)
      .then((data) => {
        Logger.debug(`DATA FROM API ${data}`);

        if (data.status.toLowerCase() === 'success') {
          setComponentType(COMPONENT.PRODUCTS);
          setMarketoInstances(data.instanceList);
          setProductsData(data.entitledProductList);
        } else {
          Logger.error(data.message);

          // Redirect to Admin Queue
          handleOnProductSelect('');
        }
      })
      .catch((err) => { Logger.error(err); errorHandler(); });
  };
  const getEntitlementByType = (message) => {
    BackendService.getEntitledProduct(message.imsAccessToken,
      message.imsUserProfile.userId, GlobalData.language,
      message.orgId, message.imsUserProfile.email, message.chatSurface, message.type)
      .then((data) => {
        Logger.debug(`DATA FROM Admin entitlement API ${data}`);

        if (data.status.toLowerCase() === 'success') {
          setFlowId(data.entitledProductList[0].flowId);
          setConnectInstanceId(data.entitledProductList[0].instanceId || '');
          setComponentType(COMPONENT.AMAZON_CHAT_WIDGET);
        } else {
          Logger.error(`Error in Admin entitlement API ${data}`);

          // Redirect to Admin Queue
          handleOnProductSelect('');
        }
      })
      .catch((err) => { Logger.error(err); errorHandler(); });
  }; 

  const validateToken = (message) => {
    const accessToken = message.imsAccessToken;
    BackendService.validateToken(accessToken)
      .then((data) => {
        setIsValidToken(data.isValid);
        if (data.isValid) {
          Logger.debug('Token is valid');

          if (message.type && message.type.toUpperCase() === 'ADMIN') {
            getEntitlementByType(message);
          } else getEntitlements(message);
        } else {
          setComponentType(COMPONENT.SERVICE_ERROR);
          Logger.error(data.message);
        }
      })
      .catch((err) => { Logger.error(err); errorHandler(); });
  };

  // Outgoing message handler - postmessage
  const postMessageHandler = (event) => {
    if (event.msgType === PostMessageUtil.outgoingMessageTypes.CONV_CLOSED) {
      setComponentType(COMPONENT.LOADING);
    }
    // Post messages to target origin
    PostMessageUtil.sendMessage(event.msgType, event.data);
  };

  const getUnAuthProduct = (language, requestOrigin, product) =>
    new Promise((resolve, reject) => {
      BackendService.getUnAuthProductList(language, requestOrigin, product)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          errorHandler();
        });
    });

  const getUnAuthMessageData = (msg) => {
    Logger.info('TYPE: ', msg.type);
    const unAuthMessage = {
      imsUserProfile: {
        userId: '',
        name: '',
        first_name: '',
        last_name: '',
        email: '',
        countryCode: '',
      },
      type: msg.type,
      // type: 'Admin',
      orgId: '',
      chatSurface: msg.chatSurface,
    };
    return unAuthMessage;
  };

  const unAuthFlow = async (msg) => {
    const allowedDomain = Config.REACT_APP_UNAUTH_WHITELISTED_DOMAIN;
    const domainFlag = allowedDomain.split(',').some((domain) => msg.chatSurface.includes(domain));
    if (domainFlag) {
      Logger.debug('Unauthenticated request received from origin: ', msg.chatSurface);
      if (msg.type.toUpperCase() === 'ADOBE_CONNECT') {
        const unAuthMessage = getUnAuthMessageData(msg);
        setMessageData(unAuthMessage);
        // getAvailableAgentCount(['adobe connect', 'Adobe Connect']);
        const productFilter = msg.type.toLowerCase().replace('_', ' ');

        try {
          const data = await getUnAuthProduct(msg.surface_locale, msg.chatSurface, productFilter);
          if (data.title.length === 0) {
            Logger.info('No product available: ', productFilter);
            errorHandler();
          } else getAvailableAgentCount(data);
        } catch (error) {
          Logger.error('Error retrieving data from getUnAuthProduct():', error);
          errorHandler();
          // ... handle the error
        }
      } else setComponentType(COMPONENT.SERVICE_ERROR);
    } else {
      Logger.error('Domain not whitelisted in Zeon: ', msg.chatSurface);
      setComponentType(COMPONENT.SERVICE_ERROR);
    }
  };

  useEffect(() => {
    const hashData = JSON.parse(decodeURIComponent(window.location.hash.substr(1)));
    PostMessageUtil.incomingMessageHandler(hashData);

    PostMessageUtil.addIncomingMessageCallback(PostMessageUtil.incomingMessageTypes.INITIAL_DATA,
      (msgType, message, source) => {
        const msg = message;
        Logger.debug('INITIAL_DATA received', {
          msgType,
          type: msg.type,
          isUnAuth: msg.isUnAuth,
          hasAccessToken: !!msg.imsAccessToken,
          uiVersion: msg.uiVersion,
        });

        GlobalData.setLocaleData(msg.surface_locale);
        Logger.info('Request Origin: ', hashData.window_origin);
        msg.chatSurface = hashData.window_origin;

        if (!getUiVersionFromUrl() && msg.uiVersion !== undefined) {
          setUiVersion(msg.uiVersion === 'v2' ? 'v2' : 'v1');
        }
        if (msg.isUnAuth === 'true' || msg.isUnAuth === true) {
          unAuthFlow(msg);
        } else if (msg.imsAccessToken && !isValidToken) {
          Logger.debug('Validating access token');
          // Remove Bearer from the token
          msg.imsAccessToken = msg.imsAccessToken.replace('Bearer ', '');
          setMessageData(msg);
          validateToken(msg);
        }

        PostMessageUtil.sendMessage(PostMessageUtil.outgoingMessageTypes.INITIAL_DATA_HANDLED);
      });

    PostMessageUtil.addIncomingMessageCallback(PostMessageUtil.incomingMessageTypes.WINDOW_OPENED,
      (msgType) => {
        Logger.debug('WINDOW_OPENED received', { msgType });
      });

    PostMessageUtil.sendMessage(PostMessageUtil.outgoingMessageTypes.CUSTOMER_AUTH_DATA_REQUEST);
  }, []);

  switch (componentType) {
    case COMPONENT.PRODUCTS:
      backgroundClass = 'welcomeBackground';
      activeComponent = (
        <div className="welcomeScreenContainer">
          <ProductPage
            onProductSelect={handleOnProductSelect}
            data={products}
          />

        </div>
      );
      break;
    case COMPONENT.MARKETO_INSTANCES:
      backgroundClass = 'welcomeBackground';
      activeComponent = (
        <div className="welcomeScreenContainer">
          <MarketoInstancePage
            onMarketoInstanceSelect={handleOnMarketoInstanceSelect}
            data={marketoInstances}
          />
        </div>
      );
      break;
    case COMPONENT.AMAZON_CHAT_WIDGET:
      backgroundClass = 'welcomeBackground';
      activeComponent = (
        <div className="chatScreen">
          <AmazonChatWidget
            userId={messageData.imsUserProfile.userId}
            fullName={messageData.imsUserProfile.name}
            firstName={messageData.imsUserProfile.first_name}
            lastName={messageData.imsUserProfile.last_name}
            // phoneNumber: messageData.imsUserProfile.,
            email={messageData.imsUserProfile.email}
            country={messageData.imsUserProfile.countryCode}
            lang={GlobalData.chatLangCode}
            entitlements=""
            issue={messageData.type}
            orgId={messageData.orgId}
            product={selectedProduct}
            postMessageHandlerFunc={postMessageHandler}
            onError={errorHandler}
            accessToken={messageData.imsAccessToken}
            persistentChat={messageData.persistentChat || true}
            contactFlowId={flowId}
            amazonConnectInstanceId={connectInstanceId}
            instanceId={selectedInstance}
            chatSurface={messageData.chatSurface}
            description={selectedProduct}
            primaryColor={uiVersion === 'v1' ? chatWidgetDefaults.PRIMARY_COLOR : chatWidgetDefaults.PRIMARY_COLOR_V2}
            uiVersion={uiVersion}
          />
        </div>
      );
      break;
    case COMPONENT.SERVICE_ERROR:
      window.addEventListener('closeOoO',()=>{
          PostMessageUtil.sendMessage(PostMessageUtil.outgoingMessageTypes.CONV_CLOSED);
      });
      backgroundClass = 'errorBackground';
      activeComponent = (
        <div className="errorScreenContainer">
          <ServiceErrorScreen />
        </div>
      );
      break;
    case COMPONENT.LOADING:
    default:
      backgroundClass = 'waitBackground';
      activeComponent = (
        <div className="waitScreenContainer">
          <ProgressCircle aria-label="Loading…" isIndeterminate top="50%" left="50%" />
        </div>
      );
      break;
  }
  // Transfer to main window after chat close
  const transferToMain = () => {
    try {
      setComponentType(COMPONENT.LOADING);
      setTimeout(() => {
        PostMessageUtil.sendMessage(PostMessageUtil.outgoingMessageTypes.CONV_CLOSED);
      }, 3000);
    } catch(error) {
      Logger.debug('Error Transfer from AWS chat');
    }
  }
  const onHeaderClose = () => {
    if (componentType === COMPONENT.PRODUCTS || componentType === COMPONENT.MARKETO_INSTANCES) {
      PostMessageUtil.sendMessage(PostMessageUtil.outgoingMessageTypes.CONV_CLOSED);
    }
  };
  // Window reselect product
  const reselectProduct = () => {
    setComponentType(COMPONENT.PRODUCTS);
  }
  const addAWSChatCloseListner = () => {
      window.addEventListener('transferToMain', transferToMain);
      window.addEventListener('closeOoO', onHeaderClose);
      window.addEventListener('reselectProduct', reselectProduct);
  }
  const removeAWSChatCloseListner = () => {
    window.removeEventListener('transferToMain', transferToMain);
    window.removeEventListener('closeOoO', onHeaderClose);
    window.removeEventListener('reselectProduct', reselectProduct);
  }

  useEffect(() => {
    if (uiVersion === 'v1') {
      document.body.classList.remove('ui-v2');
    } else {
      document.body.classList.add('ui-v2');
    }
  }, [uiVersion]);

  useEffect(() => {
    removeAWSChatCloseListner();
    addAWSChatCloseListner();
  }, []);

  return (
    <IntlProvider locale={GlobalData.locale} messages={GlobalData.translatedStrings}>
      <Provider theme={defaultTheme}>
        <div id="container" className="appContainer">
          <div id="screenContent" className="screenContent">
            <div className={backgroundClass} />
            <Header
              postMessageHandler={postMessageHandler}
              showClose={componentType !== COMPONENT.PRODUCTS && componentType !== COMPONENT.LOADING}
              uiVersion={uiVersion}
            />
            {activeComponent}
          </div>
        </div>
      </Provider>
    </IntlProvider>
  );
}
